/**
 * Options type for the number formatter function
 */
export type NumberFormatterOptions = {
  /**
   * The locale to use for the number formatter
   * @default "en-US"
   */
  locale?: Intl.LocalesArgument;
  /**
   * The minimum number of fraction digits to display
   * @default 0
   */
  minimumFractionDigits?: number;
  /**
   * The maximum number of fraction digits to display
   * @default 2
   */
  maximumFractionDigits?: number;
  /**
   * Whether to use grouping separators (e.g., thousands separator)
   * @default true
   */
  useGrouping?: boolean;
  /**
   * The notation style: "standard", "scientific", "engineering", or "compact"
   * @default "standard"
   */
  notation?: "standard" | "scientific" | "engineering" | "compact";
  /**
   * How to display the sign: "auto", "never", "always", or "exceptZero"
   * @default "auto"
   */
  signDisplay?: "auto" | "never" | "always" | "exceptZero";
};

/**
 * Helper function for formatting table cells with numbers, including i18n support
 * @param value - The value to format
 * @param options - The options for the number formatter
 * @returns The formatted number
 */
export const numberFormatter = (
  value?: number | null,
  options: NumberFormatterOptions = {},
) => {
  const {
    locale = "en-US",
    minimumFractionDigits = 0,
    maximumFractionDigits = 2,
    useGrouping = true,
    notation = "standard",
    signDisplay = "auto",
  } = options;

  return value != null
    ? value.toLocaleString(locale, {
        style: "decimal",
        minimumFractionDigits,
        maximumFractionDigits,
        useGrouping,
        notation,
        signDisplay,
      })
    : null;
};
